import { api, type Turn } from './api';

// An episode from a chaptered take (SN3, T234): one scene per chapter, cut
// to the take with its own sound and its words as the line. Used by the
// Sources bench (a demonstration) and by Spyglass (a recording the director
// read). The speaker is the workspace's default talent when there is one.

export interface ChapterLine { at: number; to: number; title: string; text: string }

/** Pure: the rundown a set of chapter lines becomes. */
export function rundownFromLines(lines: ChapterLine[], speaker: string, sourceId: string): Turn[] {
  return lines.map((l) => ({
    speaker, text: l.text, emotion: 'neutral', gesture: 'none', shot: 'wide', beat: 0.35,
    sourceId, sourceIn: l.at, sourceOut: Math.max(0.5, l.to - l.at), audio: 'sync',
    ...(l.text ? {} : { holdSeconds: Math.max(0.5, Math.round((l.to - l.at) * 10) / 10) }),
  } as Turn));
}

/** The card's title from a take's name: the take number comes off. */
export const titleFromTake = (name: string) => name.replace(/ — take \d+$/, '').replace(/^(Sonar|Spyglass) — /, '');

/** Make the card, save the rundown, gather the take; the new card's id. */
export async function episodeFromTake(o: { sourceId: string; name: string; brandId: string | null; speaker: string; chapters: { title: string }[] }): Promise<string> {
  const lines = await api.chapterLines(o.sourceId);
  if (lines.length === 0) throw new Error('mark the chapters first');
  const card = await api.createCard({ title: titleFromTake(o.name), brandId: o.brandId, topic: o.chapters.map((c) => c.title).join(' · ').slice(0, 200) });
  await api.saveRundown(card.id, rundownFromLines(lines, o.speaker, o.sourceId));
  await api.collect(card.id, { sourceId: o.sourceId, note: 'the demonstration, chaptered' }).catch(() => undefined);
  return card.id;
}
